import type { ReactNode, RefObject } from 'react';
import { useEffect, useRef } from 'react';
import { Button, IconButton } from '@launchpad-ui/components';
import { Icon } from '@launchpad-ui/icons';

import styles from './ArrayField.module.css';

/**
 * Props for ArrayFieldWrapper component.
 */
export interface ArrayFieldWrapperProps {
  /** Label for the array field */
  label?: ReactNode;
  /** Description text */
  description?: string;
  /** Error message to display for array-level validation (e.g., uniqueItems) */
  errorMessage?: string;
  /** Callback when "Add Item" is clicked */
  onAdd: () => void;
  /** Whether adding is allowed (false when maxItems reached) */
  canAdd?: boolean;
  /** Whether the array is empty */
  isEmpty?: boolean;
  /** Children (array item rows) */
  children: ReactNode;
}

/**
 * Finds the first focusable input element within a container and focuses it.
 */
export function focusFirstInput(container: HTMLElement | null): void {
  if (!container) {
    return;
  }
  const input = container.querySelector<HTMLElement>('input, textarea, select, [tabindex]:not([tabindex="-1"])');
  input?.focus();
}

/**
 * Container for array fields with header and "Add Item" button.
 */
export function ArrayFieldWrapper({
  label,
  description,
  errorMessage,
  onAdd,
  canAdd = true,
  isEmpty = false,
  children,
}: ArrayFieldWrapperProps) {
  return (
    <div className={styles.arrayField}>
      {(label || description) && (
        <div className={styles.arrayFieldHeader}>
          {label && <span className={styles.arrayFieldLabel}>{label}</span>}
          {description && <span className={styles.arrayFieldDescription}>{description}</span>}
        </div>
      )}

      {isEmpty ? (
        <div className={styles.emptyState}>
          No items yet. Click &quot;Add Item&quot; to add one.
          {errorMessage && <span className={styles.arrayFieldError}>{errorMessage}</span>}
        </div>
      ) : (
        <>
          <div className={styles.arrayItems}>{children}</div>
          {errorMessage && <span className={styles.arrayFieldError}>{errorMessage}</span>}
        </>
      )}

      <Button className={styles.addButton} variant="default" onPress={onAdd} isDisabled={!canAdd}>
        <Icon name="add" size="small" />
        Add Item
      </Button>
    </div>
  );
}

/**
 * Props for ArrayItemRow component.
 */
export interface ArrayItemRowProps {
  /** 0-based index of this item */
  index: number;
  /** Callback when remove is clicked */
  onRemove: () => void;
  /** Whether removal is allowed (false when minItems reached) */
  canRemove?: boolean;
  /** @deprecated Use autoFocus instead. Will be removed in a future release. */
  containerRef?: RefObject<HTMLDivElement | null>;
  /** Whether to auto-focus the first input in this row on mount */
  autoFocus?: boolean;
  /** The field content */
  children: ReactNode;
}

/**
 * Wrapper for each array element with remove button.
 * Uses CSS grid to align input and delete button.
 */
export function ArrayItemRow({ index, onRemove, canRemove = true, autoFocus, children }: ArrayItemRowProps) {
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (autoFocus) {
      focusFirstInput(containerRef.current);
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div className={styles.arrayItemRow} ref={containerRef}>
      <div className={styles.arrayItemContent}>{children}</div>
      <div className={styles.arrayItemRemove}>
        <IconButton
          icon="delete"
          aria-label={`Remove item ${index + 1}`}
          size="small"
          variant="minimal"
          onPress={onRemove}
          isDisabled={!canRemove}
        />
      </div>
    </div>
  );
}
